import { NotificationService } from '@myre/ui-primitive-notification';

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store, type ActionType } from '@ngrx/store';
import { of } from 'rxjs';
import { mergeMap, take, withLatestFrom } from 'rxjs/operators';

import {
  appendNotification,
  destroyNotificationError,
  destroyNotificationRequest,
  destroyNotificationSuccess,
  notifyError,
  notifyInfo,
  notifySuccess,
} from './notification.actions';
import { NotificationStatus } from './notification.interfaces';
import {
  selectFeatureCount,
  selectNotificationById,
} from './notification.reducer';

@Injectable({ providedIn: 'root' })
export class NotificationEffects {
  private _notificationService = inject(NotificationService);
  private _actions$ = inject(Actions);
  private _store = inject<Store<any>>(Store);

  public notifySuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notifySuccess),
      withLatestFrom(this._store.select(selectFeatureCount)),
      mergeMap(([action, counter]) => {
        this._notificationService.success({
          number: counter,
          message: action.message,
        });

        return of(
          appendNotification({
            id: counter,
            message: action.message,
            status: NotificationStatus.Success,
            fadein: false,
            hover: false,
          }),
        );
      }),
    ),
  );

  public notifyInfo$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notifyInfo),
      withLatestFrom(this._store.select(selectFeatureCount)),
      mergeMap(([action, counter]) => {
        this._notificationService.info({
          number: counter,
          message: action.message,
        });

        return of(
          appendNotification({
            id: counter,
            message: action.message,
            status: NotificationStatus.Info,
            fadein: false,
            hover: false,
          }),
        );
      }),
    ),
  );

  public notifyError$ = createEffect(() =>
    this._actions$.pipe(
      ofType(notifyError),
      withLatestFrom(this._store.select(selectFeatureCount)),
      mergeMap(([action, counter]) => {
        this._notificationService.error({
          number: counter,
          message: action.message,
        });

        return of(
          appendNotification({
            id: counter,
            message: action.message,
            status: NotificationStatus.Error,
            fadein: false,
            hover: false,
          }),
        );
      }),
    ),
  );

  public appendNotification$ = createEffect(() =>
    this._actions$.pipe(
      ofType(appendNotification),
      mergeMap(action => {
        return this._store.pipe(
          select(selectNotificationById(action.id)),
          take(1),
        );
      }),
      mergeMap(notification => {
        const actions: ActionType<any>[] = [];

        if (notification) {
          actions.push(
            destroyNotificationRequest({
              id: notification.id,
              changes: { ...notification, fadein: !notification.hover },
            }),
          );
        }

        return actions;
      }),
    ),
  );

  public destroyNotification$ = createEffect(() =>
    this._actions$.pipe(
      ofType(destroyNotificationRequest),
      mergeMap(action => {
        if (action.changes.fadein) {
          return of(destroyNotificationSuccess({ id: +action.id }));
        }

        return of(destroyNotificationError({ id: +action.id }));
      }),
    ),
  );
}
